import type { ReactNode } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { InlineEdit } from "@/components/design/InlineEdit";
import { PillarTabs } from "@/components/design/PillarTabs";
import { PersonLink, type PersonLinkPerson } from "@/components/common/PersonLink";
// Rein darstellende Komponente, liegt nur historisch unter components/match.
// Wenn ein dritter Aufrufer kommt, gehört sie nach components/common.
import { SaveStatus } from "@/components/match/SaveStatus";
import type { SaveState } from "@/components/match/use-match-editor";
import { BoxHeader } from "@/components/design/BoxHeader";
import { ReadOnlyBanner } from "@/components/common/ReadOnlyBanner";
import { ProjectActivity } from "./ProjectActivity";
import { Button } from "@/components/design/Button";
import { ProjectServiceMenu } from "./ProjectServiceMenu";
import type { ReadOnlyReason } from "@/lib/project-rights";
import type { Msg } from "./project-mock-data";
import type { KindTexts } from "./dashboard-kinds";

// The Project page itself, borrowed in shape from a GitLab issue: the Project is
// already there, and everything on this page describes how it can grow, never
// what is missing before it may exist.
//
// Rein darstellend und für jede Art von Projekt dieselbe (dashboard-kinds.tsx):
// Abzeichen, Texte, Pillen und die Felder der Übersicht kommen als Slots von der
// Seite, die Fläche selbst als children. Sie kennt keine Art beim Namen.

export interface ProjectActivityItem {
  id: string;
  message: Msg;
  /** ISO-Zeitpunkt des Eintrags. Angezeigt wird ein Datum, nie "vor drei Tagen". */
  at: string;
}

export function ProjectWorkspace({
  project,
  texts,
  readOnly = false,
  onEdit,
  onCommit,
  saveState,
  saveError,
  lastSavedAt,
  onRetrySave,
  activity,
  createdMeta,
  headerMeta,
  overviewFields,
  pills,
  showServices,
  company = null,
  readOnlyReason = null,
  actingAsCompanyName = null,
  onShowActivity,
  onAddBlock,
  onDelete,
  onOpenSettings,
  tabs,
  activeTab,
  onSelectTab,
  children,
}: {
  project: { title: string; description: string };
  /** Abzeichen und Beschriftungen der Art (Projekt, Ausschreibung, …). */
  texts: KindTexts;
  /** Nur ansehen, wie im Match: Werte als Text, keine Pillen (`projectRights` or listing actions). */
  readOnly?: boolean;
  /** Tippen, während getippt wird. Gespeichert wird beim Verlassen des Feldes. */
  onEdit: (patch: { title?: string; description?: string }) => void;
  /** Feld verlassen: jetzt wird geschrieben. */
  onCommit: () => void;
  saveState: SaveState;
  /** Warum der letzte Autosave scheiterte; „Speichern fehlgeschlagen" im Kopf nennt keinen Grund. */
  saveError: string | null;
  lastSavedAt: number | null;
  onRetrySave: () => void;
  activity: readonly ProjectActivityItem[];
  /** Angelegt-Angaben aus genesis; fehlen, wo genesis sie nicht verrät. */
  createdMeta: { at: string; by: PersonLinkPerson } | null;
  /** Was die Art auf derselben Zeile noch angibt (dashboard-kinds `HeaderMeta`). */
  headerMeta?: ReactNode;
  /** Die Eigenschaften der Art im ersten Abschnitt der Übersicht. */
  overviewFields: ReactNode;
  /** Flächen, die man noch holen kann, mit der Beschriftung ihrer Pille. */
  pills: readonly { key: string; label: string }[];
  /** Der Knopf für Anbindungen neben den Pillen. */
  showServices: boolean;
  /** Das Unternehmen, dem das Projekt gehört; null bei einem persönlichen Projekt. */
  company?: { id: string; name: string } | null;
  /** Warum nur lesbar, wo „dir fehlt das Recht" in die Irre führte (`projectRights`). */
  readOnlyReason?: ReadOnlyReason;
  /** Das Unternehmen, als das die Person gerade handelt; nennt der Hinweis bei `leaveCompany`. */
  actingAsCompanyName?: string | null;
  /** Führt von der Kurzfassung in der Übersicht auf die Aktivitäts-Fläche. */
  onShowActivity: () => void;
  /** Holt einen Block ins Projekt, ohne ihn schon auszufüllen. */
  onAddBlock: (key: string) => void;
  /** Öffnet die Rückfrage zum Löschen. Fehlt ohne Recht dazu. */
  onDelete?: () => void;
  /** Öffnet die Stammdaten. Dieselbe Geste wie das Zahnrad im Match. */
  onOpenSettings?: () => void;
  /** Reiter der aufgeklappten Flächen; "overview" ist immer dabei. */
  tabs: readonly { id: string; label: string }[];
  activeTab: string;
  onSelectTab: (id: string) => void;
  /** Die aufgeklappten Blöcke, von der Seite gerendert (sie hält deren Zustand). */
  children?: ReactNode;
}) {
  const intl = useIntl();
  const untitled = intl.formatMessage(texts.untitled);
  const descriptionEmpty = intl.formatMessage({
    id: "project.workspace.descriptionEmpty",
    defaultMessage: "Noch keine Beschreibung.",
  });

  return (
    <>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {/* Titel inline wie im Match-Workflow: ein durchsichtiges Feld, das
              erst beim Fokus einen Ring bekommt, und `field-sizing:content`,
              damit es so breit ist wie sein Text. Gespeichert wird beim
              Verlassen, nicht bei jedem Anschlag. */}
          <div className="flex flex-wrap items-center gap-2">
            {readOnly ? (
              <div id="project-title-readonly" className="text-smycles-display-s font-medium leading-tight tracking-tight text-ink">
                {project.title || untitled}
              </div>
            ) : (
              <input
                id="project-title"
                value={project.title}
                onChange={(e) => onEdit({ title: e.target.value })}
                onBlur={onCommit}
                aria-label={intl.formatMessage(texts.renameLabel)}
                placeholder={untitled}
                className="-mx-1 min-w-[8ch] max-w-full rounded bg-transparent px-1 text-smycles-display-s font-medium leading-tight tracking-tight text-ink placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-ink-ui [field-sizing:content]"
              />
            )}
            <span className="rounded bg-accent px-1.5 py-0.5 text-smycles-micro uppercase tracking-widest text-ink">
              {intl.formatMessage(texts.badge)}
            </span>
            <SaveStatus saveState={saveState} lastSavedAt={lastSavedAt} onRetry={onRetrySave} idPrefix="project-save" />
          </div>
          {/* div statt p: der PersonLink bringt den Avatar als <div> mit, und ein
              Block in einem Absatz ist ungültiges Markup - React bricht die Zeile
              dann im Browser mit einer Hydrationswarnung auf. */}
          <div id="project-created" className="mt-1.5 flex flex-wrap items-center gap-1.5 smycles-field-meta text-ink-muted">
            {/* Ein Unternehmensprojekt nennt wie ein Unternehmensbeitrag zuerst das Unternehmen und
                die Person dahinter nur als Herkunft, ohne eigenes Bild
                (@adr SPARK-0042-company-post-detail-shows-company-and-operator@2). */}
            {createdMeta && company ? (
              <FormattedMessage
                id="project.workspace.metaCreatedForCompany"
                defaultMessage="Angelegt am {date} für {company} von {person}"
                values={{
                  date: <FormattedDate value={createdMeta.at} day="2-digit" month="2-digit" year="numeric" />,
                  company: (
                    <Link
                      id="project-company"
                      to={`/c/${company.id}`}
                      className="font-medium text-ink underline-offset-2 hover:underline"
                    >
                      {company.name}
                    </Link>
                  ),
                  person: createdMeta.by.id ? (
                    <Link to={`/p/${createdMeta.by.id}`} className="underline-offset-2 hover:underline">
                      {createdMeta.by.name}
                    </Link>
                  ) : (
                    createdMeta.by.name
                  ),
                }}
              />
            ) : createdMeta ? (
              <FormattedMessage
                id="project.workspace.metaCreated"
                defaultMessage="Angelegt am {date} von {person}"
                values={{
                  date: <FormattedDate value={createdMeta.at} day="2-digit" month="2-digit" year="numeric" />,
                  person: <PersonLink person={createdMeta.by} size="sm" />,
                }}
              />
            ) : null}
            {createdMeta && headerMeta ? <span aria-hidden="true">·</span> : null}
            {headerMeta}
          </div>
        </div>
        {/* Löschen steht in der Kopfzeile, leise als Ghost - wie im Match. Es
            gehört zum Objekt als Ganzem, nicht zu einer seiner Flächen, und
            deshalb neben den Titel und nicht zwischen die Angebote darunter. */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {onOpenSettings ? (
            <Button
              id="project-settings-open"
              variant="icon"
              size="sm"
              onClick={onOpenSettings}
              title={intl.formatMessage(texts.settingsTitle)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </Button>
          ) : null}
          {onDelete ? (
            <Button id="project-delete-open" variant="ghost" size="sm" className="tracking-[0.2em]" onClick={onDelete}>
              <FormattedMessage id="common.delete" defaultMessage="Löschen" />
            </Button>
          ) : null}
        </div>
      </header>

      {saveError ? (
        <p id="project-save-error" role="alert" className="mt-4 smycles-field-meta text-ink">
          {saveError}
        </p>
      ) : null}

      {readOnly ? (
        <ReadOnlyBanner id="project-readonly-banner">
          {readOnlyReason ? (
          <ReadOnlyNotice
            reason={readOnlyReason}
            companyName={company?.name ?? null}
            actingAsCompanyName={actingAsCompanyName}
          />
          ) : intl.formatMessage(texts.readOnlyBanner)}
        </ReadOnlyBanner>
      ) : null}

      {/* Die Arbeitsflächen kommen als Pillen, nicht als Mängelliste in der Rail.
          Eine Pille holt ihren Block ins Projekt und verschwindet danach - was da
          ist, steht unten, was fehlt, steht oben als Angebot. */}
      {/* Die Anbindungen stehen auf DERSELBEN Zeile wie die Pillen, nicht darüber
          in der Kopfecke: beide sind Angebote an derselben Fläche, und auf
          verschiedenen Höhen sahen sie aus wie zwei verschiedene Sorten Sache.
          Die Zeile steht auch dann, wenn keine Pille übrig ist - sonst nähme das
          letzte hinzugefügte Feld den Service-Knopf mit. Nur wo die Art keine
          Anbindungen hat und nichts mehr zu holen ist, entfällt sie. */}
      {pills.length > 0 || showServices ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {pills.map((pill) => (
            <button
              key={pill.key}
              id={`project-addblock-${pill.key}`}
              type="button"
              className="smycles-chip-button"
              onClick={() => onAddBlock(pill.key)}
            >
              + {pill.label}
            </button>
          ))}
          {showServices ? (
            <div className="ml-auto">
              <ProjectServiceMenu />
            </div>
          ) : null}
        </div>
      ) : null}

      {/* EINE Box, die wächst. Vorher war jede Fläche ihre eigene Box und rechts
          stand eine zweite Spalte - das zerschnitt den Screen in Kacheln und nahm
          der Zeitachse die Breite. Jetzt ist es ein Blatt mit Abschnitten, durch
          Haarlinien getrennt, so wie die Anforderungsbox im Match wächst. */}
      <div className="mt-6 box bg-surface p-5 @sm:p-6">
        {tabs.length > 1 ? (
          <PillarTabs items={tabs} activeId={activeTab} onSelect={onSelectTab} padded={false} className="mb-5" idPrefix="project-tab" />
        ) : null}

        {/* Der Reiterwechsel tauscht die ganze Fläche. Der `key` lässt sie neu
            mounten, `reveal-rise` blendet sie dabei an - dieselbe Bewegung, mit
            der im Produkt schon Ergebnisse erscheinen. Bei reduzierter Bewegung
            bleibt sie aus; ohne das zweite Utility bliebe der Inhalt unsichtbar,
            weil `reveal-rise` bei opacity 0 startet. */}
        <div key={activeTab} className="reveal-rise motion-reduce:animate-none motion-reduce:opacity-100">
        {activeTab === "overview" ? (
          <>
        {/* Ohne eigenen Titel: der Reiter darüber heisst schon "Übersicht", und
            zweimal dasselbe Wort untereinander sagt nicht doppelt so viel.
            Der Zähler "x von y" ist ebenfalls weg - er zählte Flächen, nicht
            Fortschritt, und ein Projekt ist nicht zu 6/7 fertig, nur weil sechs
            Reiter offen sind. */}
        <Section first>{overviewFields}</Section>

        <Section
          title={intl.formatMessage({ id: "project.workspace.description", defaultMessage: "Beschreibung" })}
        >
          <div className="text-smycles-body leading-relaxed text-ink">
            {readOnly ? (
              <p id="project-description-readonly" className="whitespace-pre-line">
                {project.description || descriptionEmpty}
              </p>
            ) : (
              <InlineEdit
                id="project-description"
                multiline
                value={project.description}
                onChange={(value) => onEdit({ description: value })}
                onCommit={onCommit}
                placeholder={descriptionEmpty}
                ariaLabel={intl.formatMessage({
                  id: "project.workspace.description",
                  defaultMessage: "Beschreibung",
                })}
              />
            )}
          </div>
        </Section>

        {/* In der Übersicht nur die jüngsten Einträge: wer mehr will, geht auf
            die Fläche. Eine vollständige Chronik in der Übersicht schiebt alles
            Übrige nach unten, je länger das Projekt läuft. */}
        <Section
          title={intl.formatMessage({ id: "project.workspace.activity", defaultMessage: "Letzte Aktivität" })}
        >
          <ProjectActivity items={activity} empty={texts.activityEmpty} limit={3} onShowAll={onShowActivity} />
        </Section>
          </>
        ) : (
          children
        )}
        </div>
      </div>
    </>
  );
}

/**
 * Ein Abschnitt im Projektblatt: Haarlinie, Titel, Inhalt. Bewusst keine eigene
 * Box - verschachtelte Boxen verbietet UI_CONVENTIONS, und genau das Zerschneiden
 * in Kacheln sollte hier weg.
 */
export function Section({
  title,
  right,
  first = false,
  children,
}: {
  /** Ohne Titel entfällt der Kopf ganz, statt eine leere Zeile zu setzen. */
  title?: string;
  right?: ReactNode;
  first?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={first ? "" : "mt-6 border-t border-ink/10 pt-6"}>
      {title ? <BoxHeader title={title} right={right} /> : null}
      <div className={title ? "mt-3" : ""}>{children}</div>
    </section>
  );
}

/** Der Satz im Nur-Lesen-Banner: sagt, was hilft, statt bei einem Unternehmensprojekt eine Eigentümerin zu nennen. */
function ReadOnlyNotice({
  reason,
  companyName,
  actingAsCompanyName,
}: {
  reason: ReadOnlyReason;
  companyName: string | null;
  actingAsCompanyName: string | null;
}) {
  if (reason === "switchToCompany" && companyName) {
    return (
      <FormattedMessage
        id="project.readOnlyNeedsCompanyContext"
        defaultMessage="Gehört {company}. Wechsle in den Unternehmenskontext, um es zu bearbeiten."
        values={{ company: companyName }}
      />
    );
  }
  if (reason === "leaveCompany" && actingAsCompanyName) {
    return (
      <FormattedMessage
        id="project.readOnlyLeaveCompanyContext"
        defaultMessage="Du handelst gerade als {company}. Verlasse den Unternehmenskontext, um dein Projekt zu bearbeiten."
        values={{ company: actingAsCompanyName }}
      />
    );
  }
  if (reason === "companyInactive") {
    return (
      <FormattedMessage
        id="company.inactive.frozen"
        defaultMessage="This company is inactive, so its content is frozen. Reactivate the company to publish or change anything in its name."
      />
    );
  }
  if (reason === "companyInactiveReader") {
    return (
      <FormattedMessage id="company.inactive.frozenReader" defaultMessage="This company is inactive, so its content is frozen." />
    );
  }
  if (reason === "askCompanyAdmin" && companyName) {
    return (
      <FormattedMessage
        id="project.readOnlyAskCompanyAdmin"
        defaultMessage="Du kannst dieses Projekt nur ansehen. Bitte eine Administration von {company} um Schreibrechte."
        values={{ company: companyName }}
      />
    );
  }
  return (
    <FormattedMessage
      id="project.readOnlyBanner"
      defaultMessage="Du kannst dieses Projekt nur ansehen. Bitte die Eigentümer:in um Schreibrechte."
    />
  );
}
